import {Component, OnInit} from '@angular/core';
import {PasswordHttpService} from "../_services/password-http.service";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {ActivatedRoute, Router} from "@angular/router";
import {AlertLevel} from "../../../enums/alert-level";
import {HttpErrorResponse} from "@angular/common/http";
import {AuthenticationService} from "../../../_auth/authentication.service";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {PasswordEyeComponent} from "../../../x-components/password-eye/password-eye.component";
import {PasswordCheckComponent} from "../../../x-components/password-check/password-check.component";
import {AppRoutes} from "../../../enums/routes";
import {ACCOUNT_RESET_PARAM, PasswordResetKind, passwordResetKindFrom} from "../password-reset-kind";

/**
 * The screen a reset link lands on, for **both** credential stores (#279).
 *
 * One screen rather than two, because the interaction is identical — check the link, take a password, spend the
 * link — and only the three endpoints behind it differ. The kind rides the link itself (`?kind=account`), so it
 * is decided by whichever mail was sent rather than guessed here.
 *
 * **A link minted before #279 carries no `kind`** and resolves to the legacy flow, which is what it is. Those
 * links live two hours, so some of them are in flight across the deploy that adds this; the fall-back is what
 * keeps them working.
 */
@Component({
  selector: 'app-reset-password',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PasswordEyeComponent,
    PasswordCheckComponent
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  token: string | undefined;
  uid: string | undefined;
  error: ErrorType | undefined;
  // password reset features
  password: string = "";
  confirmation: string = "";
  valid: boolean = false;
  protected readonly ErrorType = ErrorType;

  /** Which credential store this link belongs to. */
  protected kind: PasswordResetKind = 'legacy';

  constructor(private passwordHttpService: PasswordHttpService, private gas: GlobalAlertService, private route: ActivatedRoute,
              private authenticationService: AuthenticationService, private router: Router) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
      this.uid = params['uid'];
      this.kind = passwordResetKindFrom(params['kind']);

      if (!this.token || !this.uid) {
        this.error = ErrorType.InvalidValues;
      } else {
        this.checkToken();
      }
    })
  }

  /** Whether this link is for a native CheqMe account, which the template uses for its second-factor note. */
  protected get isAccount(): boolean {
    return this.kind === 'account';
  }

  /**
   * Where "bitte erneut anfordern" goes — the forgot screen for **this** kind.
   *
   * Carrying the kind across matters: an expired native link that sent somebody to the employee form would have
   * them ask the wrong store for a mail that would never arrive, with a 200 and a reassuring banner in between.
   */
  protected get requestAgainLink(): string {
    return this.isAccount
      ? `/selfservice/forgot-password?kind=${ACCOUNT_RESET_PARAM}`
      : '/selfservice/forgot-password';
  }

  checkToken() {
    if (!this.token || !this.uid) {
      return;
    }

    this.passwordHttpService.checkToken(this.token, this.uid, this.kind).subscribe({
      next: value => {
        this.error = undefined;
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 400) {
          this.error = ErrorType.ExpiredToken
        } else if (err.status === 404) {
          this.error = ErrorType.InvalidToken
        }
      }
    })
  }

  reset() {
    if (!this.token || !this.uid || !this.valid) {
      this.gas.createAndShow("Fehler", "Werte sind fehlerhaft, bitte Eingaben überprüfen oder Link erneut aufrufen", AlertLevel.error);
      return;
    }

    this.passwordHttpService.resetPassword(this.token, this.uid, this.password, this.kind).subscribe({
      next: _ => {
        // The second factor is deliberately untouched by a reset (see native-accounts.md): a reset proves
        // control of the mailbox, which is the same kind of evidence a password is, not a second one. Saying so
        // at the moment it becomes relevant is cheaper than the support call from somebody who expected a reset
        // to be a full recovery and is now staring at a code prompt.
        this.gas.createAndShow("Erfolg",
          this.isAccount
            ? "Passwort erfolgreich geändert. Eine eingerichtete Zwei-Faktor-Authentifizierung bleibt bestehen " +
              "und wird beim nächsten Anmelden weiterhin abgefragt."
            : "Passwort erfolgreich geändert",
          AlertLevel.success);
        this.password = "";
        this.confirmation = "";
        this.redirect();
      },
      error: err => {
        console.error(err);
        this.gas.createAndShow("Fehler", "Passwort konnte nicht geändert werden", AlertLevel.error);
      }
    })
  }

  redirect() {
    this.router.navigate(this.authenticationService.isLoggedIn ? ['/' + AppRoutes.Home] : ['/' + AppRoutes.Login]).then();
  }
}

export enum ErrorType {
  InvalidValues,
  InvalidToken,
  ExpiredToken
}
